import { useMemo } from 'react';
import type { MonitoringPanelProps } from './MonitoringPanel';
import {
    useDmScanDetail,
    useDmScans,
    useMorphing,
    type DmScansQueryConfig
} from './useDmScansQueries';
import { useScanSelection } from './useScanSelection';
import { findLatestPlanningScan } from './scanTakenAt';
import type { DmScanDetail, DmScanSummary } from './types';

export type MonitoringScanPanelProps = Pick<
    MonitoringPanelProps,
    | 'scans'
    | 'scansLoading'
    | 'selectedScanId'
    | 'selectedScan'
    | 'onSelectScan'
    | 'photos'
    | 'photosLoading'
    | 'morphing'
    | 'tracksAligners'
>;

export interface MonitoringScans {
    panel: MonitoringScanPanelProps;
    detail: DmScanDetail | null | undefined;
}

export interface UseMonitoringScansArgs {
    queries: DmScansQueryConfig;
    patientId: string | null;
    dmId: number | null | undefined;
    pinToLatest: boolean;
    showMorphing: boolean;
}

function useLatestScan(scans: DmScanSummary[] | null | undefined): DmScanSummary | null {
    return useMemo(() => findLatestPlanningScan(scans ?? []), [scans]);
}

export function useMonitoringScans({
    queries,
    patientId,
    dmId,
    pinToLatest,
    showMorphing
}: UseMonitoringScansArgs): MonitoringScans {
    const { data: list, isLoading: scansLoading } = useDmScans(queries, patientId, dmId);
    const scans = list?.scans;
    // Default true: an unread flag must not strip the instruction UI off an
    // aligner case. The backend defaults the same way.
    const tracksAligners = list?.tracksAligners ?? true;
    // Pinned surfaces bypass the selection state machine entirely.
    const selection = useScanSelection(pinToLatest ? null : scans);
    const latestScan = useLatestScan(scans);

    const selectedScan = pinToLatest ? latestScan : selection.selectedScan;
    const selectedScanId = pinToLatest ? (latestScan?.scanId ?? null) : selection.selectedScanId;

    const { data: detail, isLoading: detailLoading } = useDmScanDetail(
        queries,
        patientId,
        selectedScanId
    );
    const { data: morphing, isLoading: morphingLoading } = useMorphing(
        queries,
        patientId,
        showMorphing
    );

    const panel: MonitoringScanPanelProps = {
        scans,
        scansLoading,
        selectedScanId,
        selectedScan,
        onSelectScan: selection.selectScan,
        photos: detail?.photos,
        photosLoading: scansLoading || detailLoading,
        morphing: showMorphing ? { url: morphing?.url ?? null, isLoading: morphingLoading } : null,
        tracksAligners
    };

    return { panel, detail };
}
